import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import { Avatar, Button, Typography } from "@mui/material";
import UploadInput from "../../../assets/imgs/upload-input.png";
import { useBasicInfo } from "../../../contexts/BasicInfo";
import { SUCCESS, domain } from "../../../constants/common";

import { useTeams } from "../../../contexts/Teams";
import { useAlert } from "../../../contexts/Alert";
import ImageCropperModal from "../../ImageCropperModal";
import { useImageUpload } from "../../../hooks/useImageUpload";
import { useNavigate } from "react-router-dom";
import paths from "../../../constants/paths";
import { trackClickEvent } from "../../../mixpanel";

const CompleteProfile = ({ isLicensed = true, role = null }) => {
  const { editProfilePhoto, updatePhotoStatus, updateInfoState } =
    useBasicInfo();
  const {
    organizationData,
    registerTeamMember,
    invitationState,
    registerMemberStatus,
  } = useTeams();
  const [imageFile, setImageFile] = useState(null);
  const { sendAlert } = useAlert();
  const navigate = useNavigate();

  const {
    profileImageURL,
    cropperOpen,
    setCropperOpen,
    tempImageSrc,
    error,
    handleFileInput,
    handleDrop,
    handleDragOver,
    handleCroppedImage,
    handleSkipCrop,
  } = useImageUpload({
    onUpload: (file) => setImageFile(file),
    onError: (message) => sendAlert(message, "error"),
  });

  useEffect(() => {
    if (updatePhotoStatus === SUCCESS) {
      updateInfoState("updatePhotoStatus", "");
      handleRegistration();
    }
  }, [updatePhotoStatus]);

  useEffect(() => {
    if (registerMemberStatus === SUCCESS) {
      navigate(paths.info);
    }
  }, [registerMemberStatus, navigate]);

  const handleSaveImage = async () => {
    await handleRegistration();
    if (imageFile && isLicensed) {
      await editProfilePhoto(imageFile);
    }
  };

  const handleRegistration = async () => {
    const payload = {
      ...invitationState,
      team_id: organizationData?.params?.team_id,
      role: organizationData?.params?.role,
      url_id: organizationData?.id,
      invite_as_mental_health:
        organizationData?.params?.invite_as_mental_health,
    };

    if (organizationData?.params?.email) {
      payload.email = organizationData?.params?.email;
    }
    trackClickEvent("Invitation - Complete Registration", {
      page: "Invitation Onboarding",
      button: "Continue",
      role,
    });
    await registerTeamMember(payload);
  };

  const handleGoToLogin = async () => {
    await handleRegistration();
  };

  const renderRole = () => {
    // Check organizationData params first, then fallback to role prop
    const roleValue = organizationData?.params?.role || role;
    if (!roleValue) return "";
    
    // Normalize the role value (trim whitespace, handle case variations)
    const normalizedRole = String(roleValue).trim().toLowerCase();
    
    if (normalizedRole === "team_admin") return "Admin";
    if (normalizedRole === "team_assistant") return "Assistant Admin";
    if (normalizedRole === "mental_health_provider") return "Mental Health Provider";
    
    // Fallback: return formatted role value if it doesn't match expected values
    return String(roleValue);
  };

  return (
    <Container maxWidth="sm">
      <Paper
        sx={{
          p: 4,
          display: "flex",
          flexDirection: "column",
          gap: 3,
          borderRadius: 2,
        }}
      >
        {isLicensed ? (
          <>
            <Typography variant="h3" fontWeight={600}>
              Add a profile photo
            </Typography>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "secondary.main",
                borderRadius: 2,
                backgroundColor: "background.default",
                p: 4,
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                gap: 2,
                alignItems: "center",
                cursor: "pointer",
              }}
              component="label"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <Box component="img" src={UploadInput} width={154} height={140} />
              <Typography variant="body2" lineHeight={"5px"}>
                Add your photo.
              </Typography>
              <Typography variant="body2" lineHeight={"5px"}>
                Help people get to know you on TTN.
              </Typography>
              {error && (
                <Typography variant="caption" color="error" className="p-5">
                  {error}
                </Typography>
              )}
              <Button
                color="secondary"
                variant="outlined"
                sx={{
                  color: "primary.main",
                }}
                component="span"
                disableRipple
              >
                Upload your picture
              </Button>
              <input
                style={{ display: "none" }}
                type="file"
                accept="image/*"
                onChange={handleFileInput}
              />
            </Box>
          </>
        ) : (
          <Typography variant="h3" fontWeight={600}>
            Your account is ready and connected to your team.
          </Typography>
        )}

        <Box>
          {/* 18px = 1.125rem (18 ÷ 16) */}
          <Typography
            variant="captionTitle"
            mb={2}
            component="p"
            fontWeight={600}
            style={{ fontSize: '1.125rem' }}
          >
            Preview
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
            }}
          >
            <Avatar
              sx={{
                width: 60,
                height: 60,
              }}
              src={
                isLicensed
                  ? profileImageURL
                  : organizationData?.params?.program_logo
              }
            />
            <Box>
              {isLicensed ? (
                <>      
                <Typography variant="body2" fontWeight={600}>
                    {invitationState.first_name} {invitationState.last_name}
                  </Typography>  
                 <Typography variant="caption" fontWeight={400}>
                    {renderRole()}
                  </Typography>
                  <br />
                  <Typography variant="caption" color="text.secondary">
                    {organizationData?.params?.program_name}
                  </Typography>
                </>
              ) : (
                <>
                <Typography variant="body2" fontWeight={600}>
                    {invitationState.first_name} {invitationState.last_name}
                  </Typography>
                  <Typography variant="caption" fontWeight={400}>
                    {renderRole()}
                  </Typography>
                  <br />
                  <Typography variant="caption" color="text.secondary">
                    {organizationData?.params?.program_name}
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          {isLicensed ? (
            <>
              <Button
                color="secondary"
                variant="outlined"
                sx={{
                  color: "primary.main",
                  minWidth: 90,
                }}
                component="span"
                onClick={handleGoToLogin}
                disabled={registerMemberStatus === "LOADING"}
              >
                Skip
              </Button>
              <Button
                variant="contained"
                sx={{
                  minWidth: 90,
                }}
                onClick={handleSaveImage}
                disabled={registerMemberStatus === "LOADING"}
              >
                Save
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              sx={{
                minWidth: 90,
              }}
              onClick={handleRegistration}
              disabled={registerMemberStatus === "LOADING"}
              fullWidth
            >
              Go To Your TTN Homepage
            </Button>
          )}
        </Box>

        {isLicensed && (
          <ImageCropperModal
            open={cropperOpen}
            onClose={() => setCropperOpen(false)}
            imageSrc={tempImageSrc}
            onCrop={handleCroppedImage}
            onSkip={handleSkipCrop}
          />
        )}
      </Paper>
    </Container>
  );
};

export default CompleteProfile;
